import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import {
  clearDealItemsInProgressToast,
  replaceDealItemsInProgressWithSuccess,
} from '@/lib/deal-items-toast';
import { isDealEventsSseEnabled } from '@/lib/deal-sse-flag';
import {
  GetDatasourceDocument,
  GetDatasourceQuery,
  DealItemsUpdatedV3Document,
} from '@/lib/graphql/generated/graphql';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import {
  ensureDealItemsStream,
  stopDealItemsStream,
} from '@/lib/streaming/deal-items-stream-controller';
import {
  type DealItemFromQuery,
  mergeStreamedItem,
  normalizeSingle,
} from '@/lib/streaming/normalize-deal-items';
import {
  markBackendConfirmed,
  resetValidationWorker,
} from '@/lib/validation/validation-service';
import { datasourceImportReportState } from '@/states/datasource-import-report.state';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  dealItemsRefetchState,
  productsMapState,
} from '@/states/products.state';
import {
  ALL_HIGHLIGHTABLE_FIELDS,
  type ProductModification,
  recentlyModifiedProductsState,
  updateRecentlyModifiedProducts,
} from '@/states/recently-modified-products.state';
import { datasourceProcessedSignalAtom } from '@/states/shopify-category-fallback.state';
import type { ApolloClient } from '@apollo/client';
import { useSubscription } from '@apollo/client/react';
import { useAuth0 } from '@auth0/auth0-react';
import type { TFunction } from 'i18next';
import { getDefaultStore, useSetAtom, useStore } from 'jotai';

type DealDataSource = NonNullable<
  NonNullable<GetDatasourceQuery['deal']>['dataSources'][number]
>;

async function showImportReportFromProcessedDatasource(
  client: ApolloClient,
  dealId: string,
  datasourceId: string | null | undefined,
  t: TFunction
): Promise<void> {
  try {
    const { data } = await client.query<GetDatasourceQuery>({
      query: GetDatasourceDocument,
      variables: { dealId },
      fetchPolicy: 'network-only',
    });
    const dataSources: DealDataSource[] = data?.deal?.dataSources ?? [];
    const found = datasourceId
      ? dataSources.find((dataSource) => dataSource.id === datasourceId)
      : null;
    const ds: DealDataSource | null =
      found != null ? (found as DealDataSource) : null;
    const summary =
      ds && 'importSummary' in ds && ds.importSummary ? ds.importSummary : null;
    const shouldShowImportReport = [
      'CSVDataSource',
      'WebsiteDataSource',
    ].includes(ds?.__typename ?? '');

    if (datasourceId && ds && shouldShowImportReport) {
      clearDealItemsInProgressToast({
        datasourceId,
        kind: 'datasourceProcessed',
      });
      getDefaultStore().set(datasourceImportReportState, {
        dealId,
        datasourceId,
        instructionMode: ds.instructions.instructionMode,
        itemsCreatedIds: summary?.itemsCreatedIds ?? [],
        selectedItemsUpdatedIds: summary?.selectedItemsUpdatedIds ?? [],
        excludedItemsUpdatedIds: summary?.excludedItemsUpdatedIds ?? [],
        itemsSkippedIds: [...(summary?.itemsSkippedIds ?? [])],
      });
    } else if (datasourceId) {
      replaceDealItemsInProgressWithSuccess(
        {
          title: t('general.success'),
          description: t(
            'components.deal_items_subscription.toast_datasource_completed'
          ),
        },
        {
          datasourceId,
          kind: 'datasourceProcessed',
        }
      );
    }
  } catch {
    replaceDealItemsInProgressWithSuccess(
      {
        title: t('general.success'),
        description: t(
          'components.deal_items_subscription.toast_datasource_completed'
        ),
      },
      {
        datasourceId,
        kind: 'datasourceProcessed',
      }
    );
  }
}

/** Carte dealItemId → historyId actif pour valider la pile undo contre le cache. */
function historyIdMapFromDealItems(
  items: DealItemFromQuery[]
): Map<string, string> {
  return items.reduce<Map<string, string>>((acc, item) => {
    const historyId = item.activeVersion.historyId;
    if (historyId) {
      acc.set(item.id, historyId);
    }
    return acc;
  }, new Map());
}

export function useDealItems(dealId: string) {
  const store = useStore();
  const setProductsMap = useSetAtom(productsMapState);
  const setDealItemsRefetch = useSetAtom(dealItemsRefetchState);
  const setPendingOptimisticUpdates = useSetAtom(pendingOptimisticUpdatesState);
  const setRecentlyModified = useSetAtom(recentlyModifiedProductsState);
  const { invalidateUndoForItems } = useUndoDealItems(dealId);
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  const notifyRefreshFailed = useCallback(() => {
    toast({
      title: t('general.error'),
      description: t('components.deal_items_subscription.toast_refresh_failed'),
      variant: 'destructive',
    });
  }, [t]);

  const notifyRefreshFailedRef = useRef(notifyRefreshFailed);
  notifyRefreshFailedRef.current = notifyRefreshFailed;

  const refetchDealItems = useCallback(async (): Promise<boolean> => {
    const result = await ensureDealItemsStream({
      dealId,
      isRefresh: true,
      getToken: getAccessTokenSilently,
    });
    if (result === 'error') {
      notifyRefreshFailed();
    }
    return result === 'success';
  }, [dealId, getAccessTokenSilently, notifyRefreshFailed]);

  useEffect(() => {
    const registration = { fn: refetchDealItems };
    setDealItemsRefetch(registration);
    return () => {
      setDealItemsRefetch((current) =>
        current === registration ? null : current
      );
    };
  }, [setDealItemsRefetch, refetchDealItems]);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    void ensureDealItemsStream({
      dealId,
      isRefresh: false,
      getToken: getAccessTokenSilently,
    }).then((result) => {
      if (!cancelled && result === 'error') {
        notifyRefreshFailedRef.current();
      }
    });
    return () => {
      cancelled = true;
      stopDealItemsStream(dealId);
      resetValidationWorker();
    };
  }, [dealId, getAccessTokenSilently]);

  // Subscribe to deal items updates to keep cache in sync.
  // Skipped when the SSE signal channel is enabled — in that mode a
  // coarse refetch (driven from `page-layout` via `useDealEventStream`) takes
  // over. The rich per-item merge/toast handling below is WS-only for now.
  useSubscription(DealItemsUpdatedV3Document, {
    variables: { dealId },
    skip: !dealId || isDealEventsSseEnabled(),
    onData: async ({ data: subscriptionData, client: apolloClient }) => {
      const payload = subscriptionData.data?.dealItemsUpdatedV3;
      const fromAction = payload?.fromAction;
      const updatedItems = payload?.items ?? [];

      // itemsDeleted: server returns empty items (deleted IDs no longer in DB); refetch to sync cache
      if (fromAction?.type === 'itemsDeleted') {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
        const refreshed = await refetchDealItems();
        if (refreshed) {
          replaceDealItemsInProgressWithSuccess({
            title: t('general.success'),
            description: t(
              'components.deal_items_subscription.toast_items_deleted'
            ),
          });
        }
        return;
      }

      // datasourceProcessed with empty items: resolve real result via GetDatasource and open the import report.
      if (fromAction?.type === 'datasourceProcessed' && !updatedItems.length) {
        store.set(datasourceProcessedSignalAtom, (n) => n + 1);
        void showImportReportFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
        return;
      }

      // For other actions, we need items to merge into state
      if (!updatedItems.length) return;

      const fromType = fromAction?.type;
      const shouldInvalidateDescriptionCache =
        fromType === 'datasourceProcessed' || fromType === 'itemsCreated';
      if (shouldInvalidateDescriptionCache) {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
      }

      // Jotai est la source de vérité pour l'UI — on lit la Map directement,
      // O(k) Map lookups au lieu de readQuery O(n) + writeQuery O(n) Apollo.
      const currentMap = store.get(productsMapState);
      const currentPendingUpdates = store.get(pendingOptimisticUpdatesState);
      const newItems = updatedItems
        .filter((update) => !currentMap.has(update.dealItemWithVersions.id))
        .map((update) => update.dealItemWithVersions);

      const updatedItemIds = new Set(
        updatedItems.map((u) => u.dealItemWithVersions.id)
      );
      const processedPendingIds = new Set<string>();
      currentPendingUpdates.forEach((id) => {
        if (updatedItemIds.has(id) || !currentMap.has(id))
          processedPendingIds.add(id);
      });

      setProductsMap((prev) => {
        const next = new Map(prev);

        for (const update of updatedItems) {
          const normalized = normalizeSingle(update.dealItemWithVersions);
          next.set(
            normalized.id,
            mergeStreamedItem(
              normalized,
              prev.get(normalized.id),
              currentPendingUpdates
            )
          );
        }

        return next;
      });

      if (processedPendingIds.size > 0) {
        setPendingOptimisticUpdates((prev) => {
          const next = new Set(prev);
          processedPendingIds.forEach((id) => next.delete(id));
          return next;
        });
      }

      if (fromType === 'datasourceProcessed') {
        store.set(datasourceProcessedSignalAtom, (n) => n + 1);
      }

      markBackendConfirmed(Array.from(updatedItemIds));

      // Mark products and fields as recently modified for highlighting
      // This covers: itemsUpdated, itemsCreated, datasourceProcessed
      // Backend may not send changedFields for itemsCreated/datasourceProcessed
      // (CSV, scraping, copy previous sales) — use all highlightable fields as fallback
      const isBulkImportOrCreate =
        fromAction?.type === 'itemsCreated' ||
        fromAction?.type === 'datasourceProcessed';
      const now = Date.now();
      const modifications: ProductModification[] = updatedItems.map(
        (update) => {
          const backendFields = update.changedFields ?? [];
          const fields =
            backendFields.length > 0
              ? backendFields
              : isBulkImportOrCreate
                ? ALL_HIGHLIGHTABLE_FIELDS
                : [];
          return {
            productId: update.dealItemWithVersions.id,
            fields,
          };
        }
      );

      setRecentlyModified((prev) =>
        updateRecentlyModifiedProducts(prev, now, modifications)
      );

      // Invalidate undo stack entries when items are updated by another source.
      // On ne passe que les items modifiés (pas tout le tableau) — suffisant pour
      // valider les entrées undo concernées.
      if (fromAction?.type === 'itemsUpdated') {
        const historyMap = historyIdMapFromDealItems(
          updatedItems.map((u) => u.dealItemWithVersions)
        );
        if (historyMap.size > 0) {
          invalidateUndoForItems(historyMap);
        }
      }

      // Success toasts: subscription is the single source for itemsCreated and
      // itemsDeleted. Datasource results are shown in the import report. "En cours" toasts are shown at
      // mutation launch in each caller; we never duplicate success here.
      // (itemsUpdated: no toast to avoid noise)
      if (fromAction?.type === 'itemsCreated' && newItems.length > 0) {
        const key =
          newItems.length === 1
            ? 'components.deal_items_subscription.toast_items_created_one'
            : 'components.deal_items_subscription.toast_items_created_other';
        replaceDealItemsInProgressWithSuccess(
          {
            title: t('general.success'),
            description: t(key, { count: newItems.length }),
          },
          {
            createdItemId: newItems.length === 1 ? newItems[0]?.id : undefined,
            kind: 'itemsCreated',
            fallbackToToast: false,
          }
        );
      }
      if (fromAction?.type === 'datasourceProcessed') {
        void showImportReportFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
      }
    },
  });

  return {
    refetch: refetchDealItems,
  };
}
